import { useEffect, useMemo, useState } from 'react'
import { useDiscogsConnection } from '../context/DiscogsContext'
import { useViewSort } from '../context/ViewSortContext'
import { useRecommendations, useReleases, useWantlist, useCollection } from '../hooks'
import { ReleaseFilters as ReleaseFiltersType, ReleasesResponse } from '../types/release'
import { LoadingState, ReleaseToolbar, Button } from '../components/common'
import { ReleaseFilters } from '../components/releases/ReleaseFilters'
import { ReleaseGrid } from '../components/releases/ReleaseGrid'
import { ReleaseCrateView } from '../components/releases/ReleaseCrateView'
import { cn } from '../utils'
import styles from './PageStyles.module.css'
import releaseStyles from '../components/releases/Releases.module.css'

type Source = 'random' | 'wantlist' | 'collection'

// v3: the server now drops owned/wanted records; v2 pages may still hold them.
const SHUFFLE_CACHE_PREFIX = 'tenwords:shuffle:v3'

// How many records seed one shuffle. The server accepts up to 5000 and silently
// truncated to whichever arrived first, so a 23.5k-item wantlist only ever
// recommended from the same fixed fifth of itself. Sampling here instead makes the
// choice explicit and re-draws it per refresh, so repeated refreshes explore the
// whole list.
//
// 1200 rather than the server's 5000 ceiling: measured against a real 23.5k
// wantlist, 1200 seeds return a page in 0.12s with a mean match of 81%, while 5000
// takes 3.5s for 83% — the extra seeds buy almost no quality because each one only
// contributes its few strongest neighbours anyway. Coverage doesn't suffer, since
// each refresh draws a fresh sample.
const SOURCE_SAMPLE_CAP = 1200

// Stable empty reference for the Random tab, which has no seed records — a fresh
// [] literal each render would re-run the sampling memo for nothing.
const NO_SOURCES: number[] = []

/** Random subset of `ids`, via a partial Fisher-Yates over a copy. */
function sampleIds(ids: number[], count: number): number[] {
  if (ids.length <= count) return ids
  const pool = ids.slice()
  for (let i = 0; i < count; i++) {
    const j = i + Math.floor(Math.random() * (pool.length - i))
    const tmp = pool[i]; pool[i] = pool[j]; pool[j] = tmp
  }
  return pool.slice(0, count)
}

export function ShufflePage() {
  const { isAuthenticated } = useDiscogsConnection()
  const { ids: wantlistIds } = useWantlist()
  const { ids: collectionIds } = useCollection()
  const { viewMode, setViewMode } = useViewSort()

  const [source, setSource] = useState<Source>('random')
  const [showFilters, setShowFilters] = useState(false)
  const [refreshKey, setRefreshKey] = useState(0)

  const [filters, setFilters] = useState<Omit<ReleaseFiltersType, 'release_ids' | 'q'>>(() => ({
    per_page: 50,
    shuffle: true,
  }))

  const isRandom = source === 'random'
  const allSourceIds = source === 'wantlist' ? wantlistIds : source === 'collection' ? collectionIds : NO_SOURCES
  // Re-drawn on every refresh, so a large wantlist explores a different slice of
  // itself each time instead of returning the same recommendations forever.
  const sourceIds = useMemo(
    () => sampleIds(allSourceIds, SOURCE_SAMPLE_CAP),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- refreshKey re-draws the sample
    [allSourceIds, refreshKey],
  )
  // Both seeded tabs go through the audio-neighbour graph — the difference is the
  // seed set (records you own vs records you want), not the ranking mechanism.
  // use_collection_taste stays off: it only runs when the neighbour graph returns
  // nothing, and it is the weaker signal.
  const useCollectionTaste = false
  const cacheKey = useMemo(() => {
    const stableFilters = { ...(filters as any) }
    delete stableFilters._nonce
    return `${SHUFFLE_CACHE_PREFIX}:${source}:${JSON.stringify(stableFilters)}`
  }, [filters, source])
  const [cachedData, setCachedData] = useState<ReleasesResponse | null>(() => {
    try {
      const raw = localStorage.getItem(cacheKey)
      return raw ? JSON.parse(raw) as ReleasesResponse : null
    } catch {
      return null
    }
  })

  useEffect(() => {
    try {
      const raw = localStorage.getItem(cacheKey)
      setCachedData(raw ? JSON.parse(raw) as ReleasesResponse : null)
    } catch {
      setCachedData(null)
    }
  }, [cacheKey])

  // refreshKey is included in the query key so incrementing it forces a new fetch
  const recommendations = useRecommendations(
    sourceIds,
    { ...filters, _nonce: refreshKey } as any,
    {
      enabled: isAuthenticated && !isRandom && sourceIds.length > 0 && !cachedData,
      staleTime: Infinity,
    },
    useCollectionTaste
  )
  // Random source pulls a shuffled slice of the whole catalogue (no taste profile)
  const random = useReleases(
    { ...filters, shuffle: true, _nonce: refreshKey } as any,
    {
      // Random works off the whole catalogue — no Discogs connection required
      enabled: isRandom && !cachedData,
      staleTime: Infinity,
    }
  )
  const active = isRandom ? random : recommendations
  const { data: fetchedData, isLoading, isFetching, error } = active
  const data = cachedData ?? fetchedData

  useEffect(() => {
    if (!fetchedData || cachedData) return
    try {
      localStorage.setItem(cacheKey, JSON.stringify(fetchedData))
    } catch {
      // localStorage may be full; keeping the in-memory result is still fine.
    }
  }, [cacheKey, cachedData, fetchedData])

  const handleRefresh = () => {
    try { localStorage.removeItem(cacheKey) } catch {}
    setCachedData(null)
    setRefreshKey(k => k + 1)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const needsDiscogs = !isRandom && !isAuthenticated

  return (
    <div className={styles.page}>
      <div className={styles.pageHeader}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <h1>Shuffle</h1>
          <Button
            variant="secondary"
            icon={`bi bi-arrow-clockwise${isFetching ? ' spin' : ''}`}
            onClick={handleRefresh}
            disabled={isFetching}
            title="Load a new set of recommendations"
          >
            {isFetching ? 'Loading…' : 'Refresh'}
          </Button>
        </div>
      </div>

      <div className={releaseStyles.tabBar} data-tour="shuffle-tabs">
        <button
          className={cn(releaseStyles.tab, source === 'random' && releaseStyles.tabActive)}
          onClick={() => { setSource('random') }}
        >
          <i className="bi bi-shuffle"></i> Random
        </button>
        <button
          className={cn(releaseStyles.tab, source === 'wantlist' && releaseStyles.tabActive)}
          onClick={() => { setSource('wantlist') }}
        >
          <i className="bi bi-heart-fill"></i> Wantlist
        </button>
        <button
          className={cn(releaseStyles.tab, source === 'collection' && releaseStyles.tabActive)}
          onClick={() => { setSource('collection') }}
        >
          <i className="bi bi-folder-fill"></i> Collection
        </button>
      </div>

      <ReleaseToolbar
        showFilters={showFilters}
        onToggleFilters={() => setShowFilters(!showFilters)}
        filters={filters}
        onFiltersChange={update => setFilters(prev => ({ ...prev, ...update }))}
        hideViewSort
        trailing={
          <div className={styles.viewToggle}>
            <button className={cn(styles.viewButton, viewMode === 'grid' && styles.active)} onClick={() => setViewMode('grid')} title="Cover view"><i className="bi bi-grid-3x3-gap" /></button>
            <button className={cn(styles.viewButton, viewMode === 'crate' && styles.active)} onClick={() => setViewMode('crate')} title="Crate view"><i className="bi bi-stack" /></button>
          </div>
        }
      />

      {showFilters && <ReleaseFilters filters={filters} onFiltersChange={setFilters} onClose={() => setShowFilters(false)} />}

      {needsDiscogs ? (
        <div className={styles.empty}>
          <i className="bi bi-vinyl"></i>
          <div className={styles.emptyTitle}>Connect your Discogs account</div>
          <p>Connect Discogs to get {source}-based recommendations, or use the Random tab.</p>
        </div>
      ) : error ? (
        <div className={styles.error}>
          <i className="bi bi-exclamation-triangle"></i>
          <p>Failed to load {isRandom ? 'releases' : 'recommendations'}</p>
        </div>
      ) : !isRandom && sourceIds.length === 0 ? (
        <div className={styles.empty}>
          <i className="bi bi-shuffle"></i>
          <p>No {source} data found</p>
          <p className={styles.emptyHint}>
            Sync your {source === 'wantlist' ? 'wantlist' : 'collection'} from Discogs first
          </p>
        </div>
      ) : isLoading ? (
        <LoadingState message="Finding recommendations..." />
      ) : (
        <div className={releaseStyles.releasesLayout}>
          <div className={releaseStyles.tableContainer}>
            {viewMode === 'crate' ? (
              data?.releases && data.releases.length > 0 ? (
                <ReleaseCrateView releases={data.releases} />
              ) : (
                <div className={styles.empty}>
                  <i className="bi bi-shuffle"></i>
                  <p>No recommendations found</p>
                  <p className={styles.emptyHint}>Try adjusting your filters</p>
                </div>
              )
            ) : (
              data?.releases && data.releases.length > 0 ? (
                <ReleaseGrid releases={data.releases} draggable />
              ) : (
                <div className={styles.empty}>
                  <i className="bi bi-shuffle"></i>
                  <p>No recommendations found</p>
                  <p className={styles.emptyHint}>Try adjusting your filters</p>
                </div>
              )
            )}
          </div>
        </div>
      )}
    </div>
  )
}
